/**
 * L.15 — Specialization settings drawer.
 *
 * Collapsible panel inside `SpecializationLabWindow` that exposes
 * Tinker training hyperparameters as sliders/knobs with attorney-
 * readable tooltips.
 *
 * Per L.15 D2: edits are local to the Lab; the "Re-specialize" button
 * snapshots the current values into a `tinkerTrainingJobs.request`
 * row. Same snapshot semantics as `TinkerTab.handleRetrainNow` — a
 * concurrent edit won't poison a running job.
 *
 * Per L.15 D5: strength is owned by the parent (also disabled v2);
 * this drawer doesn't render it.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { testIds } from '../../lib/testIds';
import type {
  ControlDescription,
  LabHyperparameters,
  LearningRatePreset,
  LabDatasetScope,
} from './types';

export const LAB_BASE_MODEL_OPTIONS: ReadonlyArray<string> = [
  'Qwen/Qwen3-8B',
  'Qwen/Qwen2.5-7B',
  'meta-llama/Llama-3.1-8B',
  'meta-llama/Llama-3.2-1B',
  'Qwen/Qwen3-VL',
];

export const LAB_LORA_RANK_OPTIONS: ReadonlyArray<number> = [8, 16, 32, 64];

interface DatasetScopeOption {
  key: LabDatasetScope;
  label: string;
  enabled: boolean;
  tooltip: string;
}

export const LAB_DATASET_SCOPE_OPTIONS: ReadonlyArray<DatasetScopeOption> = [
  {
    key: 'firm-skills',
    label: 'Firm Skills (committed)',
    enabled: true,
    tooltip:
      "The Skills you've checked into the firm library — the canonical worked examples NIPE already uses.",
  },
  {
    key: 'vault-notes',
    label: 'Vault notes (v2)',
    enabled: false,
    tooltip:
      'Your private Obsidian-style note vault. Reserved for a future release.',
  },
  {
    key: 'audit-log',
    label: 'NIPE audit log (RL — v2)',
    enabled: false,
    tooltip:
      'Reinforcement signal from past NIPE interactions. Reserved for a future release.',
  },
];

const LR_MIN = 1e-5;
const LR_MAX = 1e-3;
const LR_LOG_RANGE = Math.log(LR_MAX / LR_MIN);

/** Map a 0..1 slider value to a learning rate on a log scale. */
export function sliderToLearningRate(slider: number): number {
  const clamped = Math.max(0, Math.min(1, slider));
  return LR_MIN * Math.exp(clamped * LR_LOG_RANGE);
}

/** Map a learning rate back to a 0..1 slider value. */
export function learningRateToSlider(lr: number): number {
  const clamped = Math.max(LR_MIN, Math.min(LR_MAX, lr));
  return Math.log(clamped / LR_MIN) / LR_LOG_RANGE;
}

/**
 * Client-side estimate of `get_lora_lr_over_full_finetune_lr`. The
 * Python worker is the source of truth; this is just what the slider
 * displays so the user sees "we'd send ≈X". Values are rough — they
 * mirror the per-architecture factors the Tinker cookbook documents.
 */
const LR_FACTOR_TABLE: Record<string, number> = {
  'Qwen/Qwen3-8B': 30,
  'Qwen/Qwen2.5-7B': 30,
  'meta-llama/Llama-3.1-8B': 30,
  'meta-llama/Llama-3.2-1B': 50,
  'Qwen/Qwen3-VL': 30,
};
const BASE_FULL_FT_LR = 5e-6;

export function autoLearningRateFor(baseModel: string): number {
  const factor = LR_FACTOR_TABLE[baseModel] ?? 40;
  return BASE_FULL_FT_LR * factor;
}

/**
 * 2026-05-27 — Option A heuristic cost calculator.
 *
 * Hand-fit from the May 2026 smoke evidence we actually have:
 *   - meta-llama/Llama-3.2-1B, rank 4, 1 epoch  →  ~$0.50  (demo defaults, smoke #4)
 *   - Qwen/Qwen3-8B,           rank 16, 1 epoch →  ~$1.00 (per-skill-train.smoke)
 *   - Qwen/Qwen3-8B,           rank 16, 2 epoch →  ~$2.00 (handoff doc default)
 *
 * Shape: per-model base + linear scale with epochs + sub-linear scale
 * with rank (rank doubles → cost +20%, not +100%, because compute is
 * dominated by forward/backward through the frozen base weights).
 *
 * NOT a published Tinker formula — Tinker doesn't publish one.
 * Source of truth is still `tinkerSpikeRuns.costUsd` after the run
 * completes. This estimator only exists to give the slider a sensible
 * default. Update the constants as more empirical data lands.
 */
const COST_BASE_PER_EPOCH_USD: Record<string, number> = {
  'meta-llama/Llama-3.2-1B': 0.5,
  'Qwen/Qwen3-8B': 1.0,
  'Qwen/Qwen2.5-7B': 0.9,
  'meta-llama/Llama-3.1-8B': 1.0,
  'Qwen/Qwen3-VL': 1.2,
};
const COST_DEFAULT_PER_EPOCH_USD = 1.0;
/** Reference rank — costs are normalized so rank=REF → multiplier 1.0. */
const COST_RANK_REF = 16;

/** Sub-linear rank scaling. rank=4 → 0.85, rank=16 → 1.0, rank=32 → 1.10, rank=64 → 1.20. */
function rankCostMultiplier(loraRank: number): number {
  if (loraRank <= 0) return 1.0;
  const ratio = loraRank / COST_RANK_REF;
  // log2 keeps doubling-rank → +0.1 multiplier.
  return 1.0 + 0.1 * Math.log2(ratio);
}

export interface CostEstimate {
  estimateUsd: number;
  basis: string;
}

export function estimateRunCostUsd(args: {
  baseModel: string;
  loraRank: number;
  epochs: number;
}): CostEstimate {
  const basePerEpoch =
    COST_BASE_PER_EPOCH_USD[args.baseModel] ?? COST_DEFAULT_PER_EPOCH_USD;
  const rankMul = rankCostMultiplier(args.loraRank);
  const raw = basePerEpoch * Math.max(1, args.epochs) * rankMul;
  // Round to nearest $0.10 so the UI doesn't show $0.6184217.
  const estimateUsd = Math.round(raw * 10) / 10;
  const baseShort = args.baseModel.split('/').pop() ?? args.baseModel;
  const basis = `${baseShort} · rank ${args.loraRank} · ${args.epochs} epoch${args.epochs === 1 ? '' : 's'}`;
  return { estimateUsd, basis };
}

/**
 * Suggested cost-cap from an estimate. 1.5× headroom, rounded up to
 * $0.10, clamped to the slider's [0.1, 5] range.
 */
export function capFromEstimate(estimateUsd: number): number {
  const padded = estimateUsd * 1.5;
  const rounded = Math.ceil(padded * 10) / 10;
  return Math.max(0.1, Math.min(5, rounded));
}

/**
 * L.15.4 — Bridge helper. Produces the same body the NIPE
 * `describeSpecializationControls` tool returns, but with the user's
 * current values interpolated so NIPE can answer "what's my current
 * learning rate?" without guessing.
 */
export function describeControlsFromSettings(
  settings: LabHyperparameters,
): ControlDescription[] {
  const effectiveLr =
    settings.learningRatePreset === 'auto-cookbook'
      ? autoLearningRateFor(settings.baseModel)
      : settings.learningRate;
  return [
    {
      key: 'baseModel',
      label: 'Base model',
      whatItDoes:
        'The starting model — the one we layer your specialization on top of.',
      pushedUp:
        'A bigger base model is smarter and follows nuance better, but every specialization run takes longer and costs more.',
      pushedDown:
        'A smaller base model finishes specialization faster and cheaper, but the ceiling on what it can learn is lower.',
      currentValue: settings.baseModel,
    },
    {
      key: 'loraRank',
      label: 'LoRA rank',
      whatItDoes:
        'How much wiggle room the specialization has — the size of the sticky-note we attach to the model.',
      pushedUp:
        'Higher rank means the specialization can capture unusual patterns, at the cost of compute and adapter size, and more risk of overfitting if the corpus is small.',
      pushedDown:
        'Lower rank is cheap and resists overfitting, but the specialization may not be able to express subtler moves.',
      currentValue: String(settings.loraRank),
    },
    {
      key: 'epochs',
      label: 'Epochs',
      whatItDoes:
        'How many times we walk through your corpus while training. Each pass nudges the weights a bit closer to your house style.',
      pushedUp:
        'More epochs produce a stronger imprint, at the cost of compute and a higher chance of memorizing instead of generalizing.',
      pushedDown:
        'Fewer epochs are cheaper and keep the base model more dominant — useful when you want a gentle nudge, not a full transformation.',
      currentValue: String(settings.epochs),
    },
    {
      key: 'learningRate',
      label: 'Learning rate',
      whatItDoes:
        'How big a step the optimizer takes each time it nudges the weights.',
      pushedUp:
        'A higher rate makes the specialization converge faster but risks overshooting the target — the loss curve gets bumpy.',
      pushedDown:
        'A lower rate is safer and smoother, but takes more steps to reach the same point — costs more for the same final loss.',
      currentValue:
        settings.learningRatePreset === 'auto-cookbook'
          ? `${effectiveLr.toExponential(2)} (auto from cookbook)`
          : `${effectiveLr.toExponential(2)} (manual)`,
    },
    // Cost cap hidden from UI; auto-tracked internally from estimate.
    {
      key: 'datasetScope',
      label: 'Dataset scope',
      whatItDoes:
        'Which slices of your data feed the specialization corpus.',
      pushedUp:
        'Adding more scopes (when available) gives the specialization more material to learn from, but mixes signal sources.',
      pushedDown:
        'Sticking to firm Skills keeps the corpus clean and the imprint focused on canonical worked examples.',
      currentValue: settings.datasetScope.join(', '),
    },
  ];
}

interface KnobLabelProps {
  htmlFor: string;
  text: string;
  tooltipKey: string;
  attorneyTooltip: string;
  operatorTooltip: string;
}

function KnobLabel({
  htmlFor,
  text,
  tooltipKey,
  attorneyTooltip,
  operatorTooltip,
}: KnobLabelProps): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <label
      htmlFor={htmlFor}
      className="flex items-center gap-1 text-[11px] font-bold"
      title={attorneyTooltip}
    >
      {text}
      <button
        type="button"
        data-help-btn
        onClick={() => setOpen((v) => !v)}
        aria-label={`What does ${text.toLowerCase()} do?`}
      >
        ?
      </button>
      {open && (
        <div
          data-testid={testIds.specializationLab.controlTooltip(tooltipKey)}
          role="tooltip"
          className="absolute z-10 mt-5 max-w-[260px] border border-[#808080] bg-[#fffbcc] p-2 text-[10px] font-normal leading-snug text-black shadow-md"
        >
          <p>{attorneyTooltip}</p>
          <p className="mt-1 text-gray-800">{operatorTooltip}</p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-1 text-[10px] text-blue-800 underline"
          >
            close
          </button>
        </div>
      )}
    </label>
  );
}

interface RowProps {
  children: ReactNode;
}
function Row({ children }: RowProps): ReactElement {
  // Mobile: stack label + control vertically so labels read full width
  // and inputs don't get clipped. Desktop unchanged (inline label + ctl).
  return (
    <div className="relative flex items-center gap-2 py-1 max-md:flex-col max-md:items-stretch max-md:gap-0.5">
      {children}
    </div>
  );
}

export interface RespecializeStatus {
  kind: 'idle' | 'requesting' | 'kicking' | 'queued' | 'running' | 'complete' | 'error';
  message?: string;
}

interface SpecializationSettingsDrawerProps {
  settings: LabHyperparameters;
  onChange: (next: LabHyperparameters) => void;
  /** Number of skills currently in the corpus drawer; gates the
   * Re-specialize button. */
  corpusSize: number;
  /** Lifecycle of the latest re-specialize click. */
  respecializeStatus: RespecializeStatus;
  onRespecialize: () => void;
}

export function SpecializationSettingsDrawer({
  settings,
  onChange,
  corpusSize,
  respecializeStatus,
  onRespecialize,
}: SpecializationSettingsDrawerProps): ReactElement {
  // L.16 — drawer expanded by default. The whole point of the Lab is the
  // knobs; hiding them behind a click reduced discoverability.
  const [expanded, setExpanded] = useState(true);

  const update = (patch: Partial<LabHyperparameters>): void => {
    const next = { ...settings, ...patch };
    const fields: Array<keyof LabHyperparameters> = [
      'baseModel',
      'loraRank',
      'epochs',
    ];
    const touchesEstimate = fields.some((k) => k in patch);
    if (touchesEstimate) {
      const { estimateUsd } = estimateRunCostUsd({
        baseModel: next.baseModel,
        loraRank: next.loraRank,
        epochs: next.epochs,
      });
      next.costCapUsd = capFromEstimate(estimateUsd);
    }
    onChange(next);
  };

  const costEstimate = estimateRunCostUsd({
    baseModel: settings.baseModel,
    loraRank: settings.loraRank,
    epochs: settings.epochs,
  });

  const learningRateDisplay =
    settings.learningRatePreset === 'auto-cookbook'
      ? autoLearningRateFor(settings.baseModel)
      : settings.learningRate;

  const toggleDatasetScope = (
    key: LabDatasetScope,
    enabled: boolean,
  ): void => {
    if (!enabled) return;
    const next = settings.datasetScope.includes(key)
      ? settings.datasetScope.filter((k) => k !== key)
      : [...settings.datasetScope, key];
    update({ datasetScope: next.length === 0 ? ['firm-skills'] : next });
  };

  const setLearningRatePreset = (preset: LearningRatePreset): void => {
    update({ learningRatePreset: preset });
  };

  const busy =
    respecializeStatus.kind === 'requesting' ||
    respecializeStatus.kind === 'kicking' ||
    respecializeStatus.kind === 'queued' ||
    respecializeStatus.kind === 'running';

  const respecializeDisabled = busy || corpusSize === 0;

  return (
    <section
      data-testid={testIds.specializationLab.settingsDrawer}
      className="border border-solid border-[#808080] bg-[#ece9d8] text-black"
    >
      <button
        type="button"
        data-testid={testIds.specializationLab.settingsToggle}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-2 py-1 text-left text-[11px] font-bold hover:bg-[#dfdcc8]"
        aria-expanded={expanded}
      >
        <span>{expanded ? '▾' : '▸'}</span>
        <span>⚙ Specialization settings</span>
        <span className="text-[10px] font-normal text-gray-800">
          base model · rank · epochs · learning rate · dataset scope
        </span>
      </button>

      {expanded && (
        <div
          data-testid={testIds.specializationLab.settingsBody}
          className="border-t border-solid border-t-[#808080] bg-white p-2 text-[11px]"
        >
          {/* Base model */}
          <Row>
            <KnobLabel
              htmlFor="lab-base-model"
              text="Base model"
              tooltipKey="baseModel"
              attorneyTooltip="The starting model — the one we layer your specialization on top of. Bigger models are smarter but slower and more expensive to specialize."
              operatorTooltip="Passed to Tinker as `base_model`. 8B params is the sweet spot for $5 caps; 1B is a fast smoke; VL is reserved for vision-augmented Skills."
            />
            <select
              id="lab-base-model"
              data-testid={testIds.specializationLab.baseModelInput}
              value={settings.baseModel}
              onChange={(e) => update({ baseModel: e.target.value })}
              className="border border-solid border-[#808080] bg-white px-1 py-0.5 text-[11px] text-black"
            >
              {LAB_BASE_MODEL_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Row>

          {/* LoRA rank */}
          <Row>
            <KnobLabel
              htmlFor="lab-lora-rank"
              text="LoRA rank"
              tooltipKey="loraRank"
              attorneyTooltip="How much wiggle room the specialization has. Think of it as the size of the sticky-note we attach to the model — higher rank = more freedom to learn unusual patterns, also more risk of overfitting if the corpus is small."
              operatorTooltip="Tinker `rank` argument. Doubles compute and adapter file size. 8 is plenty for a single Skill; 32 is the sweet spot for a multi-Skill bundle."
            />
            <input
              id="lab-lora-rank"
              type="range"
              data-testid={testIds.specializationLab.loraRankSlider}
              min={0}
              max={LAB_LORA_RANK_OPTIONS.length - 1}
              step={1}
              value={LAB_LORA_RANK_OPTIONS.indexOf(settings.loraRank)}
              onChange={(e) => {
                const next = LAB_LORA_RANK_OPTIONS[Number(e.target.value)];
                if (next !== undefined) update({ loraRank: next });
              }}
              className="w-32"
            />
            <span
              data-testid={testIds.specializationLab.loraRankValue}
              className="w-10 text-right font-mono"
            >
              {settings.loraRank}
            </span>
          </Row>

          {/* Epochs */}
          <Row>
            <KnobLabel
              htmlFor="lab-epochs"
              text="Epochs"
              tooltipKey="epochs"
              attorneyTooltip="How many times we walk through your corpus while training. More passes = stronger imprint, more compute cost. One pass is usually enough for a single Skill."
              operatorTooltip="Python outer loop over the SFT dataset. Multiplied with `len(rows)` to set the step budget. Watch the loss curve in Settings → Tinker."
            />
            <input
              id="lab-epochs"
              type="range"
              data-testid={testIds.specializationLab.epochsSlider}
              min={1}
              max={10}
              step={1}
              value={settings.epochs}
              onChange={(e) => update({ epochs: Number(e.target.value) })}
              className="w-32"
            />
            <span
              data-testid={testIds.specializationLab.epochsValue}
              className="w-10 text-right font-mono"
            >
              {settings.epochs}
            </span>
          </Row>

          {/* Learning rate */}
          <Row>
            <KnobLabel
              htmlFor="lab-learning-rate"
              text="Learning rate"
              tooltipKey="learningRate"
              attorneyTooltip="How big a step the optimizer takes each time it nudges the weights. Too high overshoots the target; too low takes forever. 'Auto' lets the Tinker cookbook pick a known-good value for the base model you chose."
              operatorTooltip="Passed to `types.AdamParams(learning_rate=…)`. Auto mode mirrors `tinker_cookbook.hyperparam_utils.get_lora_lr_over_full_finetune_lr(base_model) × 5e-6`. Slider range 1e-5..1e-3 log-scaled."
            />
            <input
              id="lab-learning-rate"
              type="range"
              data-testid={testIds.specializationLab.learningRateSlider}
              min={0}
              max={1}
              step={0.001}
              value={learningRateToSlider(settings.learningRate)}
              onChange={(e) =>
                update({
                  learningRate: sliderToLearningRate(Number(e.target.value)),
                  learningRatePreset: 'manual',
                })
              }
              disabled={settings.learningRatePreset === 'auto-cookbook'}
              className="w-32 disabled:opacity-50"
            />
            <span
              data-testid={testIds.specializationLab.learningRateValue}
              className="w-20 text-right font-mono"
            >
              {learningRateDisplay.toExponential(2)}
            </span>
            <label className="flex items-center gap-1 text-[10px]">
              <input
                type="checkbox"
                data-testid={testIds.specializationLab.learningRateAutoToggle}
                checked={settings.learningRatePreset === 'auto-cookbook'}
                onChange={(e) =>
                  setLearningRatePreset(
                    e.target.checked ? 'auto-cookbook' : 'manual',
                  )
                }
              />
              Auto (cookbook)
            </label>
          </Row>

          {/* Cost estimate (Option A heuristic, 2026-05-27).
              Read-only line above the slider so the user sees "we
              expect this run to cost about $X" before they touch the
              cap. Tooltip on the basis spells out the inputs so the
              estimate isn't a black box. */}
          <Row>
            <span
              className="text-[11px] font-bold"
              title="Hand-fit from May 2026 smoke runs. Actual cost is recorded on tinkerSpikeRuns.costUsd after the run completes."
            >
              Estimated cost
            </span>
            <span
              data-testid={testIds.specializationLab.costEstimate}
              className="font-mono text-[11px]"
              title={`Basis: ${costEstimate.basis}`}
            >
              ≈ ${costEstimate.estimateUsd.toFixed(2)}
            </span>
            <span className="text-[10px] text-gray-700">
              ({costEstimate.basis})
            </span>
          </Row>

          {/* Dataset scope */}
          <Row>
            <KnobLabel
              htmlFor="lab-dataset-scope"
              text="Dataset scope"
              tooltipKey="datasetScope"
              attorneyTooltip="Which slices of your data feed the specialization. Today only your committed Firm Skills count; vault notes and audit-log signals are reserved for a future release."
              operatorTooltip="JSON-stringified on the jobs row as `datasetScopeJson`. Worker honors `firm-skills`, `single-skill:<name>`, `skill-bundle:<json>`."
            />
            <div className="flex flex-col text-[10px]">
              {LAB_DATASET_SCOPE_OPTIONS.map((opt) => (
                <label
                  key={opt.key}
                  data-testid={testIds.specializationLab.datasetScopeOption(opt.key)}
                  title={opt.tooltip}
                  className={`flex items-center gap-1 ${
                    opt.enabled ? '' : 'text-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!opt.enabled}
                    checked={settings.datasetScope.includes(opt.key)}
                    onChange={() => toggleDatasetScope(opt.key, opt.enabled)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </Row>

          {/* Re-specialize */}
          <div className="mt-2 flex items-center gap-2 border-t border-solid border-t-[#aca899] pt-2">
            <button
              type="button"
              data-testid={testIds.specializationLab.respecializeButton}
              onClick={onRespecialize}
              disabled={respecializeDisabled}
              className="cursor-pointer border border-[#003c74] px-3 py-1 text-[11px] font-bold text-black disabled:cursor-not-allowed disabled:opacity-50"
              style={{
                background:
                  'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)',
                borderRadius: 3,
              }}
              title={
                corpusSize === 0
                  ? 'Pick a Skill or bundle first — there has to be something to specialize on.'
                  : 'Snapshot these settings into a new specialization job. Same worker as Settings → Tinker.'
              }
            >
              ↻ Re-specialize with these settings
            </button>
            {respecializeStatus.kind !== 'idle' && (
              <span
                data-testid={testIds.specializationLab.respecializeStatus}
                className={`text-[10px] ${
                  respecializeStatus.kind === 'error'
                    ? 'font-bold text-red-800'
                    : respecializeStatus.kind === 'complete'
                      ? 'font-bold text-green-800'
                      : 'text-gray-800'
                }`}
              >
                {respecializeStatus.message ?? respecializeStatus.kind}
              </span>
            )}
          </div>
          {respecializeStatus.kind === 'error' &&
            respecializeStatus.message && (
              <div
                data-testid={testIds.specializationLab.respecializeError}
                className="mt-1 border border-red-700 bg-red-100 p-1 text-[10px] text-red-900"
              >
                {respecializeStatus.message}
              </div>
            )}
        </div>
      )}
    </section>
  );
}
